import {ActivationStatus, PackageStatus, TrustStatus} from '@sollapay/enums';
import {useMemo} from 'react';

import {useTrustAccountQuery} from '@/features/trust-accounts/hooks';

import {ACTIVATION_SORTED_PACKAGE_TYPES} from '../config';

import type {ActivationPackageDTO, PackageReadiness, TrustAccountDTO} from '@sollapay/types';

const DEFAULT_READINESS: PackageReadiness = {
  isReady: false,
  totalCount: 0,
  missingCount: 0,
  missing: []
};

export type ActivationPackageViewModel = Omit<ActivationPackageDTO, 'id' | 'ownership'>;

export type ActivationViewModel = {
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  trustAccount: TrustAccountDTO['trustAccount'] | undefined;
  packages: ActivationPackageViewModel[];
  canSendPackages: boolean;
  isActivationLocked: boolean;
  isTrustActive: boolean;
};

/**
 * Derives all activation page state from server data.
 * Packages are pre-sorted by canonical order from `ACTIVATION_SORTED_PACKAGE_TYPES`.
 * Falls back to full order with NOT_SUBMITTED defaults when no server packages are present.
 */
export function useActivationViewModel(id: string): ActivationViewModel {
  const {data, isLoading, error, refetch} = useTrustAccountQuery(id);

  const serverPackages = data?.packages;
  const packages: ActivationPackageViewModel[] = useMemo(
    () =>
      serverPackages?.length
        ? [...serverPackages].sort(
            (a, b) =>
              ACTIVATION_SORTED_PACKAGE_TYPES.indexOf(a.type) -
              ACTIVATION_SORTED_PACKAGE_TYPES.indexOf(b.type)
          )
        : ACTIVATION_SORTED_PACKAGE_TYPES.map(type => ({
            type,
            status: PackageStatus.NOT_SUBMITTED,
            canEdit: false,
            canSubmit: false,
            readiness: DEFAULT_READINESS,
            clarificationRequests: null,
            clarificationRound: 0,
            clarificationRequestedAt: null,
            clarificationRequestedBy: null
          })),
    [serverPackages]
  );

  return {
    isLoading,
    error,
    refetch,
    trustAccount: data?.trustAccount,
    packages,
    canSendPackages: data?.actions.canTriggerActivation ?? false,
    // Locked for the whole submitted→approved window, not just while pending review —
    // otherwise ops approval briefly re-opens every activation form until trustStatus catches up.
    isActivationLocked: data?.trustAccount.activationStatus !== ActivationStatus.DRAFT,
    isTrustActive: data?.trustAccount.trustStatus === TrustStatus.TRUST_ACTIVE
  };
}
